import { ArrayField, Field, JsonParseError, JsonParseErrorCode } from "sparkson";
import { Animation } from "./Animation";
import { CTA } from "./CTA";
import { Soundtrack } from "./Soundtrack";
import { Video } from "./Video";

export class ScenarioElement {
  constructor(
    @Field("id") public id: string,
    @Field("p", true) public poster?: string,
    @ArrayField("c", CTA, true) public ctas: CTA[] = [],
    @Field("pa", true) public partId: number = 1,
    @Field("mu", true) public music?: Soundtrack,
    @Field("i", true) public initial: boolean = false,
    @Field("v", true) public video?: Video,
    @Field("ns", true) public nextScenarioElementId?: string,
    @ArrayField("an", Animation, true) public animations: Animation[] = []
  ) {
    this.validate();
  }

  get hasSubtitles(): boolean {
    return this.video && this.video.subtitles.length > 0;
  }

  get isCTAOnlyElement(): boolean {
    return !this.video && !this.animations.length && !!this.ctas.length;
  }

  get isCTAOnlyPlaybackStoppingElement(): boolean {
    return !this.video && !this.animations.length && !!this.ctas.find((cta) => cta.stopsPlayback);
  }

  private validate() {
    if (!this.video) {
      if (this.animations.length > 1) {
        throw new JsonParseError("Scenario elements without video must contain at most one animation",
                                 JsonParseErrorCode.VALIDATION_FAILED);
      }
    }

    if (this.isCTAOnlyElement) {
      if (!this.ctas.length) {
        throw new JsonParseError("Scenario elements without video and animation must have at least one CTA",
                                 JsonParseErrorCode.VALIDATION_FAILED);
      }

      const hasCTAWithPoster = this.ctas.find((cta) => !!cta.poster);
      if (hasCTAWithPoster) {
        throw new JsonParseError("CTAs in scenario elements without video and animations must not have posters",
                                 JsonParseErrorCode.VALIDATION_FAILED);
      }

      const hasCTAsWithRangeTriggerType = this.ctas.find((cta) => cta.triggerType === "RANGE");
      if (hasCTAsWithRangeTriggerType) {
        throw new JsonParseError("CTAs in scenario elements without video and animations can only have 'END' trigger type",
                                 JsonParseErrorCode.VALIDATION_FAILED);
      }

      const hasCTAsStoppingPlayback = this.ctas.find((cta) => cta.stopsPlayback);
      if (!hasCTAsStoppingPlayback && !this.nextScenarioElementId) {
        throw new JsonParseError("Scenario elements without video and animation and no stopping playback CTAs must have 'nextScenarioElementId'",
                                 JsonParseErrorCode.VALIDATION_FAILED);
      }

    }

    const numberOfCTAPosters = this.ctas.filter((cta) => cta.poster).length;
    if (numberOfCTAPosters > 1) {
      throw new JsonParseError("Scenario element must have at most one CTA with poster", JsonParseErrorCode.VALIDATION_FAILED);
    }
  }
}
